/**
 * LcrPrint — page « Salaire horaire (LCR) » du PDF People.
 * Coût salarial chargé ÷ temps ouvert (TO), par personne.
 * Données : getChrYearly (même source que l'onglet Salaire horaire à l'écran).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getChrYearly } from '../../../api/chrYearly';
import { formatNumber } from '../../../utils/format';
import PrintReportHeader from './PrintReportHeader';

const fmtChf = (n) => formatNumber(Math.round(Number(n) || 0), 0);
const fmt2 = (n) => formatNumber(Number(n) || 0, 2);

export default function LcrPrint({ activeTenant, year, editedAt, onDataReady }) {
  const { t: i18nT } = useTranslation();
  const tenantId = activeTenant?.id;
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!tenantId) return;
    getChrYearly(tenantId, year).then((r) => {
      if (!r.error) setDetail(r.data);
      onDataReady && onDataReady('lcr');
    });
  }, [tenantId, year]); // eslint-disable-line

  const rows = useMemo(
    () => (detail?.rows || []).slice().sort((a, b) => (a.last_name || '').localeCompare(b.last_name || '', 'fr')),
    [detail],
  );
  const t = detail?.totals;
  const k = detail?.kpis;

  return (
    <section className="ppl-print__section">
      <PrintReportHeader category="People" title="Salaire horaire (LCR)" year={year} tenant={activeTenant} editedAt={editedAt} />

      <div className="ppl-print__kpis">
        <Kpi label={i18nT('phrases:p_18f7843233')} value={`${fmtChf(t?.loaded_payroll)} CHF`} />
        <Kpi label={i18nT('phrases:p_dcf6e0e541')} value={`${fmtChf(k?.total_heures_contractuelles)} h`} />
        <Kpi label="LCR moyen" value={`${fmt2(k?.lcr_moyen)} CHF/h`} hint={i18nT('phrases:p_ac2cbd5fd2')} accent="primary" />
      </div>

      <table className="ppl-print__table ppl-print__table--dense ppl-print__table--total-once">
        <caption>Salaire horaire par personne · {rows.length} personnes</caption>
        <colgroup>
          <col style={{ width: '60mm' }} />
          <col style={{ width: '28mm' }} />
          <col style={{ width: '34mm' }} />
          <col style={{ width: '28mm' }} />
          <col style={{ width: '30mm' }} />
        </colgroup>
        <thead>
          <tr>
            <th>Nom</th>
            <th>Dépt</th>
            <th className="num nowrap">{i18nT('phrases:p_a2b47f39e5')}</th>
            <th className="num nowrap">TO (h)</th>
            <th className="num nowrap">LCR (CHF/h)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.person_id || i} className={i % 2 === 0 ? 'zebra' : ''}>
              <td><strong>{(r.last_name || '').toUpperCase()}</strong> {r.first_name || ''}</td>
              <td>{r.department ? <span className="ppl-print__badge">{r.department}</span> : '—'}</td>
              <td className="num nowrap">{fmtChf(r.loaded_payroll)}</td>
              <td className="num nowrap">{fmtChf(r.heures_contractuelles)}</td>
              <td className="num nowrap"><strong>{fmt2(r.lcr)}</strong></td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={2} className="nowrap">Total / moyenne</td>
            <td className="num nowrap">{fmtChf(t?.loaded_payroll)}</td>
            <td className="num nowrap">{fmtChf(k?.total_heures_contractuelles)}</td>
            <td className="num nowrap">{fmt2(k?.lcr_moyen)}</td>
          </tr>
        </tfoot>
      </table>
    </section>
  );
}

function Kpi({ label, value, hint, accent }) {
  const cls = `ppl-print__kpi${accent ? ` ppl-print__kpi--${accent}` : ''}`;
  return (
    <div className={cls}>
      <div className="ppl-print__kpi-label">{label}</div>
      <div className="ppl-print__kpi-value">{value}</div>
      {hint && <div className="ppl-print__kpi-hint">{hint}</div>}
    </div>
  );
}
